import { capitalize } from 'lodash';
import React from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { ActionsMenu } from '../../components/ActionsMenu/ActionsMenu';
import { Action } from '../../types/Action';
import { User } from '../../types/User';
import { Comment } from './types';
import { testIsViewerUser } from './utils';

interface Props {
  comment: Comment;
  viewer: User;
  onCopyLink: (comment: Comment) => void;
  onDelete: (comment: Comment) => void;
  onResolveComment: (commentData: Comment) => void;
  onUnresolveComment: (commentData: Comment) => void;
  onUpdate: (comment: Comment) => void;
  onFilterAction?: (action: Action, comment: Comment) => Action;
}

type CommentActionsType =
  | 'copyLink'
  | 'delete'
  | 'edit'
  | 'resolve'
  | 'unresolve';

const getTooltipMessage = (
  action: CommentActionsType,
  comment: Comment,
  viewer: User,
  intl: IntlShape,
) => {
  switch (action) {
    case 'edit':
      if (comment.isResolved)
        return intl.formatMessage({
          id: 'CommentActions.cannotEditResolved',
          defaultMessage: 'Cannot edit a resolved comment',
          description: 'Tooltip for edit action when comment is resolved',
        });
      if (!testIsViewerUser(comment.createdBy, viewer))
        return intl.formatMessage({
          id: 'CommentActions.onlyOwnerEdit',
          defaultMessage: 'Only the owner can edit this comment',
          description: 'Tooltip for edit action when viewer is not owner',
        });
      return intl.formatMessage({
        id: 'CommentActions.editComment',
        defaultMessage: 'Edit the comment',
        description: 'Tooltip for edit action',
      });
    case 'resolve':
    case 'unresolve':
      if (!!comment.threadId)
        return intl.formatMessage(
          {
            id: 'CommentActions.cannotResolveReply',
            defaultMessage: 'Cannot {action} a reply comment',
            description:
              'Tooltip for resolve/unresolve action on reply comment. Example: action = "resolve"',
          },
          {
            action,
          },
        );
      return intl.formatMessage(
        {
          id: 'CommentActions.actionComment',
          defaultMessage: '{action} a comment',
          description: `Tooltip for action. Example: action = "Resolve"`,
        },
        {
          action: capitalize(action),
        },
      );
    case 'delete':
      if (!testIsViewerUser(comment.createdBy, viewer))
        return intl.formatMessage({
          id: 'CommentActions.onlyOwnerDelete',
          defaultMessage: 'Only the owner can delete this comment',
          description: 'Tooltip for delete action when viewer is not owner',
        });
      return intl.formatMessage({
        id: 'CommentActions.deleteComment',
        defaultMessage: 'Delete the comment',
        description: 'Tooltip for delete action',
      });
    case 'copyLink':
      if (comment.isResolved)
        return intl.formatMessage({
          id: 'CommentActions.unresolveToCopyLink',
          defaultMessage: 'Unresolve this comment to copy the link',
          description: 'Tooltip for copy link action when comment is resolved',
        });
      return intl.formatMessage({
        id: 'CommentActions.copyLink',
        defaultMessage: 'Copy the link to this comment',
        description: 'Tooltip for copy link action',
      });
    default:
      return '';
  }
};

export const CommentActions = ({
  comment,
  viewer,
  onCopyLink,
  onDelete,
  onFilterAction,
  onResolveComment,
  onUnresolveComment,
  onUpdate,
}: Props) => {
  const intl = useIntl();
  const { createdBy, isResolved, threadId } = comment;

  const actions: Record<CommentActionsType, (comment: Comment) => void> = {
    edit: onUpdate,
    copyLink: onCopyLink,
    delete: onDelete,
    resolve: onResolveComment,
    unresolve: onUnresolveComment,
  };

  const isViewerUser = testIsViewerUser(createdBy, viewer);

  const actionsLabel = [
    {
      label: isResolved
        ? intl.formatMessage({
            id: 'CommentActions.unresolve',
            defaultMessage: 'Unresolve',
            description: 'Unresolve action label',
          })
        : intl.formatMessage({
            id: 'CommentActions.resolve',
            defaultMessage: 'Resolve',
            description: 'Resolve action label',
          }),
      value: isResolved
        ? intl.formatMessage({
            id: 'CommentActions.unresolveValue',
            defaultMessage: 'unresolve',
            description: 'Unresolve action value',
          })
        : intl.formatMessage({
            id: 'CommentActions.resolveValue',
            defaultMessage: 'resolve',
            description: 'Resolve action value',
          }),
      disabled: !!threadId,
      tooltip: getTooltipMessage(
        isResolved ? 'unresolve' : 'resolve',
        comment,
        viewer,
        intl,
      ),
    },
    {
      label: intl.formatMessage({
        id: 'CommentActions.edit',
        defaultMessage: 'Edit',
        description: 'Edit action label',
      }),
      value: 'edit',
      disabled: isResolved || !isViewerUser,
      tooltip: getTooltipMessage('edit', comment, viewer, intl),
    },
    {
      label: intl.formatMessage({
        id: 'CommentActions.copyLinkLabel',
        defaultMessage: 'Copy link',
        description: 'Copy link action label',
      }),
      value: 'copyLink',
      disabled: isResolved,
      tooltip: getTooltipMessage('copyLink', comment, viewer, intl),
    },
    {
      label: intl.formatMessage({
        id: 'CommentActions.delete',
        defaultMessage: 'Delete',
        description: 'Delete action label',
      }),
      value: 'delete',
      disabled: !isViewerUser,
      tooltip: getTooltipMessage('delete', comment, viewer, intl),
    },
  ];

  const activeActions = onFilterAction
    ? actionsLabel.map((action) => onFilterAction(action, comment))
    : actionsLabel;

  return (
    <ActionsMenu
      enablePortal
      actions={activeActions}
      name="comment actions"
      onActionClick={(actionType) => {
        const action = actions[actionType as CommentActionsType];
        action?.(comment);
      }}
    />
  );
};
